import type { FC, ReactNode } from 'react'
import { createContext, useState, useMemo, useContext, useEffect } from 'react'

import type { Locale, WhiteLabel } from '@skytech/types'
import { DEFAULT_LOCALE, getWhiteLabelFromParams, isLocale } from '@skytech/utils'
import { useLocation } from 'react-router-dom'

type AppContext = {
  locale: Locale
  whiteLabel: WhiteLabel
  isEditTranslationsMode: boolean
  setLocale: (v: Locale) => void
  setWhiteLabel: (v: WhiteLabel) => void
  setIsEditTranslationsMode: (v: boolean) => void
  isMobileNavBar: boolean
  setIsMobileNavBar: (v: boolean) => void

  isActiveEmployeeConsentModalOpen: boolean
  setIsActiveEmployeeConsentModalOpen: (v: boolean) => void
  isWizardMode: boolean
  setIsWizardMode: (v: boolean) => void
}

const Context = createContext<AppContext>({
  locale: DEFAULT_LOCALE,
  whiteLabel: 'skytech',
  isEditTranslationsMode: false,
  setLocale: () => {},
  setWhiteLabel: () => {},
  setIsEditTranslationsMode: () => {},
  isMobileNavBar: false,
  setIsMobileNavBar: () => {},
  isActiveEmployeeConsentModalOpen: false,
  setIsActiveEmployeeConsentModalOpen: () => {},
  isWizardMode: false,
  setIsWizardMode: () => {},
})

export const AppContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()

  let browserLocale = navigator.language.split(/[-_]/)[0]
  // To have all Norwegian translations under one key
  if (browserLocale === 'nb') browserLocale = 'no'

  const storedLocale = getStoredLocale()
  const defaultLocale = storedLocale || (browserLocale as Locale) || DEFAULT_LOCALE
  const [locale, setLocale] = useState<Locale>(defaultLocale)
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabel>('skytech')
  const [isEditTranslationsMode, setIsEditTranslationsMode] = useState(false)
  const [isWizardMode, setIsWizardMode] = useState(false)

  const handleSetWhiteLabel = (_wl: WhiteLabel) => {
    setWhiteLabel(_wl)
    // todo: remove when the organisation is specified in the url
    localStorage.setItem('org', _wl)
    // todo: find a way to keep the org info only in one place
    document.getElementsByTagName('html')[0].classList.add(_wl)
  }

  const handleSetLocale = (l: Locale) => {
    setLocale(l)
    // To preselect locale on the login page
    localStorage.setItem('locale', l)
  }

  useEffect(() => {
    getWhiteLabelFromParams({ location, setWhiteLabel: handleSetWhiteLabel })
  }, [location])

  const [isMobileNavBar, setIsMobileNavBar] = useState(false)

  const [isActiveEmployeeConsentModalOpen, setIsActiveEmployeeConsentModalOpen] = useState(false)

  const value: AppContext = useMemo(
    () => ({
      locale,
      whiteLabel,
      isEditTranslationsMode,
      setLocale: handleSetLocale,
      setWhiteLabel: handleSetWhiteLabel,
      setIsEditTranslationsMode,
      isMobileNavBar,
      setIsMobileNavBar,
      isActiveEmployeeConsentModalOpen,
      setIsActiveEmployeeConsentModalOpen,
      isWizardMode,
      setIsWizardMode,
    }),
    [
      isEditTranslationsMode,
      locale,
      whiteLabel,
      isMobileNavBar,
      isActiveEmployeeConsentModalOpen,
      isWizardMode,
    ]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useAppContext = () => useContext(Context)

function getStoredLocale() {
  const storedLocale = localStorage.getItem('locale')
  if (!storedLocale) return null

  if (isLocale(storedLocale)) return storedLocale as Locale

  return null
}
